import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrderShipments } from '@/api/domains/order-shipments';
import type { OrderShipmentFilters } from '@/api/types/order-shipments';
import type { Shipment } from '@/api/types/shipments';
import { BulkActionBar } from '@/components/bulk-action-bar';
import { usePageTitle } from '@/components/layout/page-title';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { BatchCreateShipmentsDialog } from './BatchCreateShipmentsDialog';
import { LabelFormatDialog } from './LabelFormatDialog';
import { OrderShipmentsTable } from './OrderShipmentsTable';
import {
  EMPTY_FILTER_VALUES,
  ShipmentsFilters,
  type ShipmentsFilterValues,
} from './ShipmentsFilters';

const PER_PAGE_OPTIONS = [5, 10, 25, 50];

function toApiFilters(
  values: ShipmentsFilterValues,
  page: number,
  perPage: number,
): OrderShipmentFilters {
  return {
    status: values.status,
    event_code: values.event_code,
    service_type: values.service_type,
    tracking_number: values.tracking_number || null,
    reference: values.reference || null,
    tag: values.tag || null,
    order_number: values.order_number || null,
    email: values.email || null,
    phone: values.phone || null,
    missing_point: values.missing_point ? 1 : null,
    date_from: values.date_from || null,
    date_to: values.date_to || null,
    shipment_date_from: values.shipment_date_from || null,
    shipment_date_to: values.shipment_date_to || null,
    page,
    per_page: perPage,
  };
}

export function ShipmentsPage() {
  const { t } = useTranslation('shipments');
  usePageTitle(t('title'));

  const [applied, setApplied] = useState<ShipmentsFilterValues>(EMPTY_FILTER_VALUES);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [batchOpen, setBatchOpen] = useState(false);

  const filters = useMemo(() => toApiFilters(applied, page, perPage), [applied, page, perPage]);
  const query = useOrderShipments(filters);
  const [downloadTarget, setDownloadTarget] = useState<Shipment[] | null>(null);

  const rows = useMemo(() => query.data?.data ?? [], [query.data]);
  const meta = query.data?.meta;
  const hasFilters = Object.values(applied).some(
    (value) => value !== null && value !== '' && value !== false,
  );

  const toggleExpand = (orderId: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });

  const toggleSelect = (orderId: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });

  const allSelected = rows.length > 0 && rows.every(({ order }) => selected.has(order.id));

  const toggleSelectAll = () =>
    setSelected(allSelected ? new Set() : new Set(rows.map(({ order }) => order.id)));

  // Shipments of the selected orders that actually have a carrier label to print
  // (limited to the current page - selection state only holds order ids).
  const selectedShipments = useMemo(
    () =>
      rows
        .filter(({ order }) => selected.has(order.id))
        .flatMap(({ shipments }) => shipments)
        .filter((shipment) => shipment.carrier_shipment_id !== null),
    [rows, selected],
  );

  // Orders eligible for the "create shipments" bulk action: selected orders that
  // have no shipment yet. Orders that already have at least one shipment are
  // excluded - they don't count towards the action (shipments are added per
  // order from the order view, not re-created in bulk).
  const creatableOrders = useMemo(
    () =>
      rows
        .filter(({ order, shipments }) => selected.has(order.id) && shipments.length === 0)
        .map(({ order }) => order),
    [rows, selected],
  );
  const creatableOrderIds = useMemo(
    () => creatableOrders.map((order) => order.id),
    [creatableOrders],
  );
  // Predefined package sizes are per destination market - the batch dialog
  // offers the union for the selected orders' destinations.
  const creatableDestinationCountries = useMemo(
    () => [...new Set(creatableOrders.map((order) => order.destination_country_code ?? null))],
    [creatableOrders],
  );

  const downloadLabels = (shipments: Shipment[]) => {
    const eligible = shipments.filter((shipment) => shipment.carrier_shipment_id !== null);
    if (eligible.length === 0) return;

    setDownloadTarget(eligible);
  };

  return (
    <div className="space-y-9">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-4xl font-medium tracking-[-0.9px] text-black">{t('title')}</h2>
        {/* Visual parity with the design; the tour itself is not implemented yet. */}
        <Button variant="outline" className="border-black text-black hover:bg-accent" type="button">
          {t('quickTour')}
        </Button>
      </div>

      <ShipmentsFilters
        values={applied}
        onApply={(values) => {
          setApplied(values);
          setPage(1);
        }}
      />

      <div className="space-y-6">
        <div className="relative">
          <OrderShipmentsTable
            rows={rows}
            isLoading={query.isLoading}
            selected={selected}
            onToggleSelect={toggleSelect}
            allSelected={allSelected}
            onToggleSelectAll={toggleSelectAll}
            expanded={expanded}
            onToggleExpand={toggleExpand}
            onDownloadLabels={downloadLabels}
            emptyState={
              <div className="py-10 text-center text-sm text-muted-foreground">
                {hasFilters ? t('emptyFiltered') : t('empty')}
              </div>
            }
          />

          <BulkActionBar
            count={selected.size}
            label={t('batch.selected', { count: selected.size })}
            onClear={() => setSelected(new Set())}
            clearLabel={t('batch.clearSelection')}
          >
            <Button
              variant="outline"
              size="sm"
              type="button"
              disabled={selectedShipments.length === 0}
              onClick={() => downloadLabels(selectedShipments)}
            >
              {t('batch.downloadLabels', { count: selectedShipments.length })}
            </Button>
            <Button
              size="sm"
              type="button"
              disabled={creatableOrderIds.length === 0}
              onClick={() => setBatchOpen(true)}
            >
              {t('batch.trigger', { count: creatableOrderIds.length })}
            </Button>
          </BulkActionBar>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-sm leading-5 text-black">{t('table.resultsPerPage')}</span>
            <Select
              value={String(perPage)}
              onValueChange={(v) => {
                setPerPage(Number(v));
                setPage(1);
              }}
            >
              <SelectTrigger className="h-9 w-16 rounded-[10px] border-[#e5e5e5] bg-white px-3 shadow-none focus:ring-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PER_PAGE_OPTIONS.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="flex h-9 items-center justify-center rounded-[10px] border border-[#e5e5e5] bg-white px-4 text-sm font-medium leading-5 text-foreground hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
            >
              {t('table.previous')}
            </button>
            <button
              type="button"
              disabled={meta ? page >= meta.last_page : true}
              onClick={() => setPage((p) => p + 1)}
              className="flex h-9 items-center justify-center rounded-[10px] border border-[#e5e5e5] bg-white px-4 text-sm font-medium leading-5 text-foreground hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
            >
              {t('table.next')}
            </button>
          </div>
        </div>
      </div>

      <BatchCreateShipmentsDialog
        open={batchOpen}
        onOpenChange={setBatchOpen}
        orderIds={creatableOrderIds}
        destinationCountries={creatableDestinationCountries}
        onSuccess={() => setSelected(new Set())}
      />

      <LabelFormatDialog shipments={downloadTarget} onClose={() => setDownloadTarget(null)} />
    </div>
  );
}
